import { ETextStyleVariant, Text } from '@outdoorsyco/bonfire';
import React, { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import AddressInput from '@/components/common/AddressInput/AddressInput';
import { ESearchFilters } from '@/constants/searchFilters';
import { ILocationSearchAutocompleteListOption } from '@/hooks/useLocationSearchAutocompleteOptions';
import { EFilterMethod, setFormFilter } from '@/redux/modules/searchForm';
import { getSearchResultsMeta } from '@/redux/selectors/search/meta';
import { getSearchFormDeliveryFilter } from '@/redux/selectors/search/searchForm';
import { retrieveMapboxFullAddress } from '@/services/retrieveMapboxFullAddress';
import {
  EDeliveryFields,
  EDeliveryOption,
  TDeliveryFields,
} from '@/services/types/core/delivery.types';

interface IBringItToMeFilterProps {
  onUserInteraction?: () => void;
  storeOriginalSearchValues?: () => void;
}

const BringItToMeFilter: React.FC<IBringItToMeFilterProps> = ({
  onUserInteraction,
  storeOriginalSearchValues,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const form = useForm<TDeliveryFields>();

  const {
    [ESearchFilters.DELIVERY_ADDRESS]: deliveryAddressFromStore,
    [ESearchFilters.DELIVERY]: isDeliveryFromStore,
  } = useSelector(getSearchFormDeliveryFilter);

  const searchMeta = useSelector(getSearchResultsMeta);
  const location = searchMeta ? { lng: searchMeta.lng, lat: searchMeta.lat } : undefined;

  const [addressValue, setAddressValue] = useState<string>(
    isDeliveryFromStore && deliveryAddressFromStore ? deliveryAddressFromStore : '',
  );

  const handleAddressChange = useCallback(
    (inputValue: string) => {
      setAddressValue(inputValue);
      form.setValue(EDeliveryFields.DELIVERY_ADDRESS, inputValue);
    },
    [form],
  );

  const handleSelectAddress = useCallback(
    async (option: ILocationSearchAutocompleteListOption) => {
      if (!option) {
        return;
      }

      storeOriginalSearchValues?.();

      const { label, value } = option;
      const selectedAddress = value?.name || value?.address || label;

      setAddressValue(selectedAddress);
      form.setValue(EDeliveryFields.DELIVERY_ADDRESS, selectedAddress);

      const mapboxId = value?.mapbox_id || value?.id;

      let fullMapboxAddress = '';
      if (mapboxId) {
        fullMapboxAddress = await retrieveMapboxFullAddress(value);
      }

      const deliveryFilters = {
        [ESearchFilters.ADDRESS]: selectedAddress,
        [ESearchFilters.DELIVERY]: true,
        [ESearchFilters.DELIVERY_ADDRESS]: selectedAddress,
        [ESearchFilters.DELIVERY_STATIONARY]: EDeliveryOption.MOVING,
        ...(mapboxId && { [ESearchFilters.MAPBOX_ID]: mapboxId }),
        ...(fullMapboxAddress && {
          [ESearchFilters.FULL_MAPBOX_ADDRESS]: fullMapboxAddress,
        }),
      };

      dispatch(
        setFormFilter({
          filters: deliveryFilters,
          filterMethod: EFilterMethod.INPUT,
        }),
      );

      onUserInteraction?.();
    },
    [dispatch, form, onUserInteraction, storeOriginalSearchValues],
  );

  return (
    <>
      <Text variant={ETextStyleVariant.MediumRegular} className="mb-6 text-gray-50">
        {intl.formatMessage({
          defaultMessage:
            'Enter a location or address to browse RVs that can be brought to you. Let the host drop off the vehicle at your house, an airport, or somewhere else, so you can hit the road from there.',
          id: 'Pqw3EN',
          description: 'Bring it to me filter modal description',
        })}
      </Text>

      <div className="mb-6">
        <AddressInput
          form={form}
          required={false}
          addressValue={addressValue}
          name={EDeliveryFields.DELIVERY_ADDRESS}
          onChangeAddress={handleAddressChange}
          onSelectAddress={handleSelectAddress}
          placesOption={{
            location: [location?.lng || 0, location?.lat || 0],
            types: 'address,region,place,district,locality,poi',
          }}
          label={intl.formatMessage({
            defaultMessage: 'Enter location or address',
            id: 'rG8Jtc',
          })}
          onFocus={e => e.target.select()}
        />
      </div>
    </>
  );
};

export default BringItToMeFilter;
